/**
 * Error Handling Utility
 * 
 * This utility provides functions for consistent error handling across the application.
 * It includes error normalization, logging, and user feedback.
 */

import config from '../configs/env';
import toastManager from './toastManager';

/**
 * Normalize error response structure
 * 
 * @param {Error} error - The error object
 * @returns {Object} Normalized error object
 */
export const normalizeError = (error) => {
 if (!error) {
 return { message: 'Unknown error occurred', status: 500, data: null };
 }

 // Axios error
 if (error.response) {
 return {
 message: error.response.data?.message || error.response.data?.error || 'Server error',
 status: error.response.status,
 data: error.response.data,
 originalError: error
 };
 }

 // Network error
 if (error.request) {
 return {
 message: 'Network error. Please check your connection.',
 status: 0,
 data: null,
 originalError: error
 };
 }

 // Other errors
 return {
 message: error.message || 'An unexpected error occurred',
 status: 500,
 data: null,
 originalError: error
 };
};

/**
 * Log error to console and potentially to error tracking service
 * 
 * @param {string} context - Context where the error occurred
 * @param {Error|Object} error - The error object
 * @param {Object} additionalInfo - Additional information about the error
 */
export const logError = (context, error, additionalInfo = {}) => {
 const normalizedError = error.status ? error : normalizeError(error);
 
 // Log to console in development
 if (config.isDevelopment || config.debug) {
 console.error(`[${context}]`, normalizedError, additionalInfo);
 }
 
 // In production, we would send to error tracking service
 if (config.isProduction && window.errorTrackingService) {
 window.errorTrackingService.captureException(normalizedError.originalError || error, {
 tags: {
 context,
 status: normalizedError.status
 },
 extra: {
 ...additionalInfo,
 message: normalizedError.message,
 data: normalizedError.data
 }
 });
 }
};

/**
 * Handle API errors with appropriate user feedback
 * 
 * @param {Error} error - The error object
 * @param {string} defaultMessage - Default error message to show
 * @param {Object} options - Options for error handling
 * @returns {Object} Normalized error object
 */
export const handleApiError = (error, defaultMessage = 'An error occurred', options = {}) => {
 const normalizedError = normalizeError(error);
 const {
 showToast = true,
 logToConsole = true,
 redirectOn401 = true,
 context = 'API Request'
 } = options;

 // Log error
 if (logToConsole) {
 logError(context, normalizedError);
 }

 // Show toast notification
 if (showToast) {
 toastManager.showError(normalizedError, defaultMessage);
 }

 // Handle authentication errors
 if (normalizedError.status === 401 && redirectOn401) {
 // This will be handled by the AxiosInterceptors component
 // But we can trigger a logout action here if needed
 if (window.dispatchLogout) {
 window.dispatchLogout();
 }
 }

 return normalizedError;
};

/**
 * Create a custom error with additional properties
 * 
 * @param {string} message - Error message
 * @param {Object} properties - Additional properties
 * @returns {Error} Custom error object
 */
export const createError = (message, properties = {}) => {
 const error = new Error(message);
 Object.assign(error, properties);
 return error;
};

/**
 * Format validation errors from API responses
 * 
 * @param {Object} errors - Validation errors object from API
 * @returns {Object} Formatted errors object for form display
 */
export const formatValidationErrors = (errors) => {
 if (!errors) return {};
 
 // If errors is already in the right format, return it
 if (typeof errors === 'object' && !Array.isArray(errors)) {
 return errors;
 }
 
 // If errors is an array, convert to object
 if (Array.isArray(errors)) {
 return errors.reduce((acc, error) => {
 if (error.field && error.message) {
 acc[error.field] = error.message;
 }
 return acc;
 }, {});
 }
 
 // If errors is a string, return generic error
 if (typeof errors === 'string') {
 return { _error: errors };
 }
 
 return { _error: 'Validation failed' };
};

/**
 * Handle form submission errors
 * 
 * @param {Error} error - The error object
 * @param {Function} setErrors - Function to set form errors
 * @param {Object} options - Options for error handling
 */
export const handleFormError = (error, setErrors, options = {}) => {
 const normalizedError = normalizeError(error);
 const {
 showToast = true,
 logToConsole = true,
 context = 'Form Submission'
 } = options;
 
 // Log error
 if (logToConsole) {
 logError(context, normalizedError);
 }
 
 // Show toast for non-validation errors
 if (showToast && (!normalizedError.data || !normalizedError.data.errors)) {
 toastManager.showError(normalizedError, 'Form submission failed');
 }
 
 // Set form errors
 if (normalizedError.data && normalizedError.data.errors) {
 // Format validation errors
 const formattedErrors = formatValidationErrors(normalizedError.data.errors);
 setErrors(formattedErrors);
 } else {
 // Set generic error
 setErrors({ _error: normalizedError.message || 'Form submission failed' });
 }
};

export default {
 normalizeError,
 logError,
 handleApiError,
 createError,
 formatValidationErrors,
 handleFormError
};